/* SignupSuccess.jsx · celebration screen shown after the wizard
 * completes. Extracted from /pages/SignupWizard.jsx for component
 * breakdown.  */

import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { PartyPopper } from "lucide-react";
import { useTranslation } from "react-i18next";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";

export default function SignupSuccess({ success, compact, onComplete }) {
  const { t } = useTranslation();
  const nav = useNavigate();

  const body = (
    <main
      className={
        compact
          ? "px-1 py-3 text-center"
          : "min-h-[70vh] max-w-2xl mx-auto px-5 py-16 lg:py-24"
      }
      data-testid="signup-success-page"
    >
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className={compact ? "text-center px-2" : "wiz-step-card p-8 sm:p-10 text-center"}
      >
        {/* Confetti-y sparkle ring · pure decorative */}
        <motion.div
          initial={{ scale: 0.4, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6, type: "spring", stiffness: 220, damping: 18 }}
          className="mx-auto h-20 w-20 rounded-full grid place-items-center mb-6 relative"
          style={{
            background: "color-mix(in srgb, var(--brand) 16%, transparent)",
            border: "1px solid color-mix(in srgb, var(--brand) 45%, transparent)",
            boxShadow: "0 0 0 8px color-mix(in srgb, var(--brand) 10%, transparent), 0 0 30px color-mix(in srgb, var(--brand) 35%, transparent)",
          }}
        >
          <PartyPopper size={34} className="text-[color:var(--brand)]" strokeWidth={2.2} />
          {[0, 1, 2, 3].map((i) => (
            <motion.span
              key={i}
              className="absolute h-1.5 w-1.5 rounded-full"
              style={{ background: "var(--brand)" }}
              initial={{ opacity: 0 }}
              animate={{
                opacity: [0, 1, 0],
                x: [0, Math.cos((i / 4) * Math.PI * 2) * 50],
                y: [0, Math.sin((i / 4) * Math.PI * 2) * 50],
              }}
              transition={{ duration: 1.4, delay: 0.3 + i * 0.08, repeat: Infinity, repeatDelay: 1.6 }}
            />
          ))}
        </motion.div>
        <h1 className="text-[28px] sm:text-[34px] font-semibold tracking-tight text-[color:var(--wiz-text-1)] leading-tight">
          {t("signupSuccess.welcome", "Welcome aboard")}{success.name ? `, ${success.name}` : ""}.
        </h1>
        <p className="mt-3 text-[15px] leading-relaxed text-[color:var(--wiz-text-2)] max-w-md mx-auto">
          {t("signupSuccess.intro", "Your account is created. Check")}{" "}
          <span className="text-[color:var(--brand)] font-semibold">{success.email}</span>{" "}
          {t("signupSuccess.verifyTail", "for the verification link · set your password and you're live.")}
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Button asChild data-testid="signup-success-cta-funding" className="font-semibold">
            <a href="https://live.ofinancial.markets/login" target="_blank" rel="noopener noreferrer">
              {t("signupSuccess.cta", "Go to your client area")}
            </a>
          </Button>
          <Button
            variant="ghost"
            onClick={() => (onComplete ? onComplete() : nav("/"))}
            data-testid="signup-success-cta-home"
            className="text-[color:var(--wiz-text-2)] hover:text-[color:var(--wiz-text-1)] hover:bg-[color:color-mix(in_srgb,var(--wiz-text-1)_6%,transparent)]"
          >
            {compact ? t("signupSuccess.close", "Close") : t("signupSuccess.backHome", "Back to home")}
          </Button>
        </div>
        <div className="mt-7 text-[12px] text-[color:var(--wiz-text-3)]">
          {t("signupSuccess.didntSee", "Didn't see the email? Check spam, or")}{" "}
          <Link to="/contact" className="underline hover:text-[color:var(--wiz-text-2)]">{t("signupSuccess.contactSupport", "contact support")}</Link>.
        </div>
      </motion.div>
    </main>
  );

  if (compact) return body;
  return (
    <div className="relative overflow-x-hidden">
      <Navbar />
      {body}
      <Footer />
    </div>
  );
}
